import { useSearchParamsState } from 'hooks/useSearchParamState';
import { useStore } from 'hooks/useStore';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const useUnconstraintForecast = () => {
  const {
    UserStore: { userModulesRoles },
    UnconstrainedForecastViewStore: {
      isConceptLoading,
      conceptDetails,
      isunconstrainedForecastDataLoading,
      directcurrentNDCIndex,
      indirectcurrentNDCIndex,
      directNDCheading,
      indirectNDCheading,
      unconstrainedForecastData,
      isInvoiceSalesLoading,
      invoiceSalesSummary,
      isUnconstrainedForecastNotesDataLoading,
      unconstrainedForecastStatus,
      isUnconstrainedForecastProgressLoading,
      unconstrainedForecastPlanningPeriods,
      unconstrainedPeriod,
      unconstrainedForecastPlanningPeriodLoading,
      forecastBtnLabel,
      UnconstrainedForecastStatusLoading,
      fetchForecastGFCNotes,
      handleGetConceptDetails,
      setDirectCurrentTabIndex,
      setIndirectCurrentTabIndex,
      setUnconstrainedForecastPeriod,
      contractDetails,
      UnconstrainedForecastNotesData,
    },
  } = useStore();
  const [_gfcParam, setGfcParam] = useSearchParamsState('gfc', '');
  const [ndcParam, _setNdcParam] = useSearchParamsState('ndc', '');
  const [isDirectMode, setIsDirectMode] = useState<boolean>(true);

  const handleDirectCurrentTabIndex = (e: { index: number }) => {
    setDirectCurrentTabIndex(e.index);
  };

  const handleIndirectNDCTabChange = (e: { index: number }) => {
    setIndirectCurrentTabIndex(e.index);
  };

  const [searchParams] = useSearchParams();
  const searchValue = searchParams.get('gfc');

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    fetchForecastGFCNotes(
      isDirectMode
        ? directNDCheading[directcurrentNDCIndex]?.ndc
        : indirectNDCheading[indirectcurrentNDCIndex]?.ndc,
      signal
    );

    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional deps
  }, [directcurrentNDCIndex, indirectcurrentNDCIndex, isDirectMode]);

  useEffect(() => {
    if (searchValue) {
      handleGetConceptDetails(parseFloat(searchValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // get future 12 months date
  const getFutureMonthsView = () => {
    const [year, month] = unconstrainedPeriod.code.split('-');
    const arr: { label: string; dateString: string }[] = [];
    const startMonth = Number(month);
    const startYear = Number(year);
    const monthShortHand = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];

    for (let i = 0; i < 15; i++) {
      const month = (startMonth + i) % 12;
      const year = startYear + Math.floor((startMonth + i) / 12);
      arr.push({
        label: `${monthShortHand[month]}-${year}`,
        dateString: `${year}-${month >= 9 ? month + 1 : `0${month + 1}`}-01`,
      });
    }

    return arr;
  };
  const forecastingMonthsView: { label: string; dateString: string }[] = getFutureMonthsView();

  // utility function to get sales details
  const getInvoiceSalesDetails = (ndc: string, want: string) => {
    if (invoiceSalesSummary.length === 0) return;

    const ndcSummary = invoiceSalesSummary.filter(item => item.NDC11 === ndc);

    if (ndcSummary.length === 0) {
      return 'NA';
    } else {
      if (want === 'ytd' && ndcSummary[0].YTDAverage) {
        return ndcSummary[0].YTDAverage;
      } else if (want === '3ma' && ndcSummary[0].ThreeMonthAverage) {
        return ndcSummary[0].ThreeMonthAverage;
      } else if (want === 'pm' && ndcSummary[0].LastMonthAverage) {
        return ndcSummary[0].LastMonthAverage;
      } else {
        return 'NA';
      }
    }
  };

  // utility function to get strategy code for NDC
  const getStratCode = (ndc: string, forecastMonth: string) => {
    const ndcForecast = unconstrainedForecastData?.forecasts.filter(item => item.NDC11 === ndc)[0];

    const forecast = ndcForecast?.forecast_details.filter(
      item => item.ForecastPeriod === forecastMonth
    );

    if (forecast && forecast?.length > 0) {
      return forecast[0]?.StratCode;
    } else {
      return '-';
    }
  };

  // utility function to get baseline values
  const getBaseline = (ndc: string, forecastMonth: string) => {
    const ndcForecast = unconstrainedForecastData?.forecasts.filter(item => item.NDC11 === ndc)[0];

    const forecast = ndcForecast?.forecast_details.filter(
      item => item?.ForecastPeriod === forecastMonth
    );

    if (forecast && forecast.length > 0) {
      const val = forecast[0]?.WholesalerDemandForecast;
      return typeof val === 'number' ? val.toLocaleString('en-US') : val;
    } else {
      return '-';
    }
  };

  // utility function to get adjustment values
  const getAdjustments = (ndc: string, forecastMonth: string) => {
    const ndcForecast = unconstrainedForecastData?.forecasts.filter(item => item.NDC11 === ndc)[0];

    const forecast = ndcForecast?.forecast_details.filter(
      item => item.ForecastPeriod === forecastMonth
    );

    if (forecast && forecast.length > 0) {
      const val = forecast[0]?.UnconstrainedForecast;
      return typeof val === 'number' ? val.toLocaleString('en-US') : val;
    } else {
      return '-';
    }
  };

  const handleOptionChange = (option: { label: string; code: string }) => {
    setUnconstrainedForecastPeriod(option);
  };

  useEffect(() => {
    if (conceptDetails && conceptDetails.gfc) setGfcParam(conceptDetails.gfc.toString());
    // setNdcParam(ndcParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync gfc to URL
  }, [conceptDetails]);

  useEffect(() => {
    setDirectCurrentTabIndex(0);
    if (directNDCheading.length > 0 && ndcParam) {
      for (let i = 0; i < directNDCheading.length; i++) {
        if (directNDCheading[i].ndc === ndcParam) {
          setDirectCurrentTabIndex(i);
          setIsDirectMode(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync tab to ndc param
  }, [directNDCheading, ndcParam]);

  useEffect(() => {
    setIndirectCurrentTabIndex(0);
    if (indirectNDCheading.length > 0 && ndcParam) {
      for (let i = 0; i < indirectNDCheading.length; i++) {
        if (indirectNDCheading[i].ndc === ndcParam) {
          setIndirectCurrentTabIndex(i);
          setIsDirectMode(false);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync tab to ndc param
  }, [indirectNDCheading, ndcParam]);

  return {
    userModulesRoles,
    unconstrainedForecastData,
    forecastingMonthsView,
    isConceptLoading,
    isInvoiceSalesLoading,
    conceptDetails,
    isunconstrainedForecastDataLoading,
    directcurrentNDCIndex,
    indirectcurrentNDCIndex,
    directNDCheading,
    indirectNDCheading,
    isUnconstrainedForecastNotesDataLoading,
    unconstrainedForecastStatus,
    isUnconstrainedForecastProgressLoading,
    unconstrainedForecastPlanningPeriods,
    unconstrainedPeriod,
    unconstrainedForecastPlanningPeriodLoading,
    forecastBtnLabel,
    UnconstrainedForecastStatusLoading,
    handleDirectCurrentTabIndex,
    handleIndirectNDCTabChange,
    getInvoiceSalesDetails,
    getStratCode,
    getBaseline,
    getAdjustments,
    handleOptionChange,
    isDirectMode,
    setIsDirectMode,
    contractDetails,
    UnconstrainedForecastNotesData,
  };
};
